'use client';

import { XIcon } from '@phosphor-icons/react';
import type { ResolvedColumn } from '@/shared/mobile-service/lib/columns';
import { CellValue } from './renderers';
import { SlideOverLayer } from './SlideOver';

/**
 * Right-side detail pane opened by clicking a table row. Lists every field for
 * the artifact (grid columns plus curated content fields such as a body/text).
 */
export function ContentPane({
  row,
  fields,
  title,
  onClose,
}: {
  row: Record<string, unknown>;
  fields: ResolvedColumn[];
  title: string;
  onClose: () => void;
}) {
  return (
    <SlideOverLayer onClose={onClose}>
      <aside className="flex h-full w-full max-w-xl flex-col border-l border-white/10 bg-[#0f1d32] shadow-xl ring-1 ring-white/10">
        <header className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white/70"
            aria-label="Close"
          >
            <XIcon size={18} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {fields.map((f) => {
            const value = row[f.field];
            const isLongText =
              f.isContent ||
              (typeof value === 'string' && value.length > 160) ||
              (value != null && typeof value === 'object');

            return (
              <section key={f.field} className="mb-5">
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-white/65">
                  {f.label}
                </h3>
                {f.description && (
                  <p className="mb-1 text-xs text-white/50">{f.description}</p>
                )}
                {value == null || value === '' ? (
                  <p className="text-sm text-white/50">—</p>
                ) : isLongText || f.render === 'nested_table' || f.render === 'list' ? (
                  <pre className="whitespace-pre-wrap break-words font-sans text-sm text-white/90">
                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                  </pre>
                ) : (
                  <div className="text-sm text-white/90">
                    <CellValue col={f} value={value} />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </aside>
    </SlideOverLayer>
  );
}
